import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { LinkedinTypes } from 'enums';
import { FunnelFunctions, NodeVariants } from 'features/co-pilot/co-pilot.types';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Node } from 'reactflow';
import { makeStyles } from 'tss-react/mui';
import PaiButton from '../../ui/base-components/pai-button';
import { LinkedinAccount } from '../linkedin-accounts/linkedin-accounts.types';

interface Props {
  open: boolean;
  nodes: Node[];
  linkedinAccount: LinkedinAccount | undefined;

  handleClose: () => void;
}

const FlowIncompleteDialog: React.FC<Props> = ({ open, nodes, linkedinAccount, handleClose }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const notEveryMessageNodeHasFunctionName = !_.every(
    _.filter(nodes, (node) => node.type === NodeVariants.FUNCTION_NODE),
    (node) => node.data.functionName
  );

  const notEveryConditionNodeHasFunctionName = !_.every(
    _.filter(nodes, (node) => node.type === NodeVariants.CONDITIONAL_NODE || node.type === NodeVariants.CONDITION_WITH_DURATION_NODE),
    (node) => node.data.functionName
  );

  const inMailNodes = nodes.filter(
    (node) =>
      node.type === NodeVariants.FUNCTION_NODE &&
      (node.data.functionName === FunnelFunctions.SEND_IN_MAIL || node.data.functionName === FunnelFunctions.SEND_IN_MAIL_WITH_CONTEXT)
  );

  const emailNodes = nodes.filter(
    (node) =>
      node.type === NodeVariants.FUNCTION_NODE &&
      (node.data.functionName === FunnelFunctions.SEND_EMAIL ||
        node.data.functionName === FunnelFunctions.SEND_EMAIL_WITH_CONTEXT ||
        node.data.functionName === FunnelFunctions.SEND_FOLLOW_UP_EMAIL)
  );

  const inMailsAreAllowed = () => {
    if (_.isEmpty(inMailNodes)) return true;

    if (linkedinAccount?.liProductType === LinkedinTypes.LINKEDIN) return false;

    return true;
  };

  const emailsAreAllowed = () => {
    if (_.isEmpty(emailNodes)) return true;

    if (_.isNil(linkedinAccount)) return false;

    const configOk = !_.isNil(linkedinAccount.emailConfig) &&
      !linkedinAccount.emailConfig.accessRevoked;

    const accountsOk = !_.isEmpty(linkedinAccount?.emailAccounts) &&
      linkedinAccount?.emailAccounts?.some(ea => !ea.accessRevoked);

    return configOk || accountsOk;
  };

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('flowIncompleteDialogTitle')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={1}>
          <Typography className={classes.text}>{t('flowIncompleteDialogContent')}</Typography>
          {notEveryMessageNodeHasFunctionName ? (
            <Typography className={cx(classes.text, classes.warningText)}>{t('warningMissingMessageType')}</Typography>
          ) : undefined}
          {notEveryConditionNodeHasFunctionName ? (
            <Typography className={cx(classes.text, classes.warningText)}>{t('warningMissingConditionType')}</Typography>
          ) : undefined}
          {!inMailsAreAllowed() ? <Typography className={cx(classes.text, classes.warningText)}>{t('inMailsNotAllowed')}</Typography> : undefined}
          {!emailsAreAllowed() ? <Typography className={cx(classes.text, classes.warningText)}>{t('emailsNotAllowed')}</Typography> : undefined}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiButton text={t('ok')} size={'small'} border={'rounded'} action={handleClose} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
  warningText: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
}));

export default FlowIncompleteDialog;
